import { inject, provideAppInitializer, EnvironmentProviders, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, Routes } from '@angular/router';
import { ServicioConfiguracionRutasDinamicas } from '../services/routing/configuracion-rutas-dinamicas.service';
import { OpcionesInicializadorRutas } from './rutas-dinamicas.initializer.types';

const INDICE_NO_ENCONTRADO = -1;
const LONGITUD_MINIMA_RUTAS = 0;
const POSICION_INICIAL = 1;

let rutasYaInicializadasEnNavegador = false;
const pathsRutasDinamicasAgregadas = new Set<string>();

function validarRutasCargadas(rutas: Routes): Routes {
  if (!rutas || !Array.isArray(rutas)) {
    return [];
  }

  return rutas.filter((ruta) => {
    const tienePathValido = typeof ruta.path === 'string' && ruta.path.trim() !== '';
    const tieneComponenteOLoader = ruta.component !== undefined || ruta.loadComponent !== undefined;

    if (!tienePathValido) {
      return false;
    }

    if (!tieneComponenteOLoader) {
      return false;
    }

    return true;
  });
}

function limpiarRutasDinamicasPrevias(configuracion: Routes): void {
  if (pathsRutasDinamicasAgregadas.size === LONGITUD_MINIMA_RUTAS) {
    return;
  }

  for (let i = configuracion.length - POSICION_INICIAL; i >= LONGITUD_MINIMA_RUTAS; i--) {
    const path = configuracion[i].path;
    if (path && pathsRutasDinamicasAgregadas.has(path)) {
      configuracion.splice(i, POSICION_INICIAL);
    }
  }

  pathsRutasDinamicasAgregadas.clear();
}

function registrarPathsAgregados(rutas: Routes): void {
  for (const ruta of rutas) {
    if (ruta.path) {
      pathsRutasDinamicasAgregadas.add(ruta.path);
    }
  }
}

function insertarRutasEnRouter(router: Router, rutasValidas: Routes): boolean {
  const configuracionActual = router.config;

  limpiarRutasDinamicasPrevias(configuracionActual);

  const indiceRutaSlug = configuracionActual.findIndex((ruta) => ruta.path === ':slug');
  const indiceRutaCatchAll = configuracionActual.findIndex((ruta) => ruta.path === '**');

  if (indiceRutaSlug === INDICE_NO_ENCONTRADO) {
    return false;
  }

  const rutaSlug = configuracionActual[indiceRutaSlug];
  configuracionActual.splice(indiceRutaSlug, POSICION_INICIAL);

  registrarPathsAgregados(rutasValidas);

  const posicionInsercion =
    indiceRutaCatchAll > indiceRutaSlug ? indiceRutaCatchAll - POSICION_INICIAL : indiceRutaCatchAll;

  if (posicionInsercion !== INDICE_NO_ENCONTRADO) {
    configuracionActual.splice(posicionInsercion, LONGITUD_MINIMA_RUTAS, ...rutasValidas, rutaSlug);
  } else {
    configuracionActual.push(...rutasValidas, rutaSlug);
  }

  router.resetConfig(configuracionActual);
  return true;
}

export function inicializarRutasDinamicas(opciones: OpcionesInicializadorRutas): () => Promise<void> {
  const { servicioConfigRutas, router, platformId } = opciones;

  return async () => {
    const esNavegador = platformId ? isPlatformBrowser(platformId) : true;

    if (!esNavegador) {
      const rutasFallbackServidor = validarRutasCargadas(servicioConfigRutas.generarRutasFallback());
      if (rutasFallbackServidor.length > LONGITUD_MINIMA_RUTAS) {
        insertarRutasEnRouter(router, rutasFallbackServidor);
      }
      return;
    }

    if (rutasYaInicializadasEnNavegador) {
      return;
    }

    const rutasFallback = validarRutasCargadas(servicioConfigRutas.generarRutasFallback());
    if (rutasFallback.length > LONGITUD_MINIMA_RUTAS) {
      insertarRutasEnRouter(router, rutasFallback);
    }
    rutasYaInicializadasEnNavegador = true;
  };
}

export function resetearEstadoInicializador(): void {
  rutasYaInicializadasEnNavegador = false;
  pathsRutasDinamicasAgregadas.clear();
}

export function proveedorInicializadorRutasDinamicas(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const servicioConfigRutas = inject(ServicioConfiguracionRutasDinamicas);
    const router = inject(Router);
    const platformId = inject(PLATFORM_ID);

    return inicializarRutasDinamicas({
      servicioConfigRutas,
      router,
      platformId,
    })();
  });
}
